
import {
  ref,
  defineComponent,
  watch,
  computed,
} from 'vue';
import { ElForm, ElMessage, ElTree } from 'element-plus';
import { useDictionaryStore } from '@/store/dictionary';
import { cloneDeep } from 'lodash-es';

import {
  regionAreaSaveApi,
  regionAreaDetailApi,
  regionAreaUpdateApi,
} from '@/views/system-config/regional-setting/api';
import { IZoneAreaSaveReq } from '@/views/system-config/regional-setting/api/types';
import { useDictionary } from '@/hooks/use-dict';

interface TreeItem {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  children: TreeItem[];
}

export default defineComponent({
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    regionId: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'handelSuccess'],
  setup(props, { emit }) {
    const formRef = ref<InstanceType<typeof ElForm> | null>(null);
    const treeRef = ref<InstanceType<typeof ElTree>>();
    const region = ref(''); // 所属区域
    const form = ref<IZoneAreaSaveReq>({
      regionName: '', // 区域名称
      regionInfoList: [], // 省和城市集合
    });

    // 区域code
    const regionCodeList = computed(() => {
      return useDictionary(['pims_regionbelong']).batchDictListMap.value.pims_regionbelong;
    });

    // 省市区数据（重组）
    const provinceCityAreaList = ref<TreeItem[]>([]);
    const copyProvinceCityAreaList = ref<TreeItem[]>([]);
    const handleProvinceCityArea = (list: TreeItem[]): TreeItem[] => {
      return list?.map((item: any) => {
        if (item.provinceName) {
          item.label = item.provinceName;
          item.children = item.cities;
          if (item.cities.length) {
            item.cities.forEach((info: any) => {
              info.parentName = item.provinceName;
              info.parentCode = item.code;
              info.label = info.cityName;
              info.children = null; // 不显示区
              if (info.areas.length) {
                info.areas.forEach((area: any) => {
                  area.label = area.areaName;
                  area.children = null;
                });
              }
            });
          }
        }
        return item;
      });
    };

    // 默认勾选
    const checkedKeys = ref<string[]>([]);
    const expandedKeys = ref<string[]>([]);
    const handleDefault = () => {
      checkedKeys.value = [];
      expandedKeys.value = [];
      form.value.regionInfoList!.forEach((info, index) => {
        if (info.cityInfoList?.length) {
           info.cityInfoList!.forEach((e) => {
             checkedKeys.value.push(e.cityCode!);
             //  expandedKeys.value.push(e.cityCode!);
             // 台湾省
             if (!e.cityCode) {
               checkedKeys.value.push(info.provinceCode!);
               //  expandedKeys.value.push(info.provinceCode!);
             }
           });
        } else {
          form.value.regionInfoList?.splice(index, 1);
        }
      });
      checkedKeys.value = checkedKeys.value.filter(e => e);
      // expandedKeys.value = expandedKeys.value.filter(e => e);
    };

    // 获取详情
    const handleOpen = async () => {
      if (!useDictionaryStore().provinceList.length) {
        await useDictionaryStore().getProvince();
        const list = useDictionaryStore().provinceList;
        provinceCityAreaList.value = handleProvinceCityArea(list);
      } else {
        provinceCityAreaList.value = handleProvinceCityArea(useDictionaryStore().provinceList);
      }
      copyProvinceCityAreaList.value = cloneDeep(provinceCityAreaList.value);

      if (props.regionId) {
        const res = await regionAreaDetailApi(props.regionId);
        const { regionCode, regionId, regionInfoList, regionName } = res?.data;
        form.value = {
          regionCode,
          regionId,
          regionInfoList,
          regionName,
        };
      }
    };

    const handleCheck = (val: any, item: any) => {
      // 删除省
      if (form.value.regionInfoList?.map(e => e.provinceCode).includes(val.code)) {
        const _index = form.value.regionInfoList?.findIndex(key => key.provinceCode === val.code);
        if (_index !== -1) {
          form.value.regionInfoList?.splice(_index!, 1);
        }
      }

      // 删除市
      if (!Array.isArray(val) && val?.parentName) {
        const provinceItem = form.value.regionInfoList?.find(e => e.provinceCode === val.parentCode);
        if (provinceItem) {
          const key = provinceItem.cityInfoList?.findIndex(e => e.cityCode === val.code);
          if (key !== -1) {
            provinceItem.cityInfoList?.splice(key!, 1);
          }
        }
      }

      // 标记是否全选
      const obj: Record<string, any[]> = {};
      if (item.checkedNodes.length) {
        item.checkedNodes.forEach((info: any) => {
          // isOneProvince标记是否全选
          if (info?.provinceName && info?.children.length) {
            info.children[0].isOneProvince = '1';
          }
          if (item?.halfCheckedNodes.find((e: TreeItem) => e.label === info.parentName)) {
            info.isOneProvince = '0';
          }
          if (info.cityName) {
            if (obj[info.parentName]) {
              obj[info.parentName].push(info);
            } else {
              obj[info.parentName] = [info];
            }
          }
          // 台湾省，没有城市配置的
          if (info?.provinceName && !info?.children.length) {
            obj[info.provinceName] = [
              {
                parentName: info.label,
                parentCode: info.code,
                isOneProvince: 1,
                children: null,
              },
            ];
          }
        });
      }

      Object.values(obj)?.forEach((info) => {
        // 判断省重复
        if (!form.value.regionInfoList?.find((e: any) => e.provinceCode === info[0].parentCode)) {
          form.value.regionInfoList?.push({
            provinceName: info[0].parentName,
            provinceCode: info[0].parentCode,
            isOneProvince: info[0].isOneProvince,
            cityInfoList: info.map((e: any) => {
              return {
                cityCode: e.code,
                cityName: e.cityName,
              };
            }),
          });
        } else {
          // 判断市重复
          const provinceItem = form.value.regionInfoList.find(e => e.provinceCode === info[0].parentCode);
          if (info.length) {
            info.forEach((e: any) => {
              if (!provinceItem?.cityInfoList?.map(city => city.cityCode).includes(e.code)) {
                provinceItem?.cityInfoList?.push({
                  cityCode: e.code,
                  cityName: e.cityName,
                });
              }
            });
          }
        }

        form.value.regionInfoList?.forEach((e) => {
          if (e.provinceName === info[0].parentName) {
            e.isOneProvince = info[0].isOneProvince;
          }
        });
      });
      handleDefault();
    };

    // 选择区域
    const handleSelectRegion = (val: string) => {
      const item = regionCodeList.value.find(info => info.valueCode === val);
      form.value.regionName = item ? item.value : '';
    };

    const list = ref<TreeItem[]>([]);
    const isSearch = ref(false);
    watch(() => region.value, (val: string) => {
      isSearch.value = false;
      list.value = [];
      if (val) {
        provinceCityAreaList.value = cloneDeep(copyProvinceCityAreaList.value);
        // 匹配省
        list.value = copyProvinceCityAreaList.value.filter((item) => {
          return item.label.indexOf(val) !== -1;
        });
        isSearch.value = !!list.value.length;
        // 匹配市
        if (!isSearch.value) {
          copyProvinceCityAreaList.value.forEach((item) => {
            item.children.forEach((info) => {
              if (info.label.indexOf(val) !== -1) {
                isSearch.value = true;
                list.value.push(item);
              }
            });
          });
        }
        // 匹配区
        // if (!isSearch.value) {
        //   copyProvinceCityAreaList.value.forEach((item: any) => {
        //     item.children.forEach((info: any) => {
        //       info.children.forEach((area: any) => {
        //         if (area.label.indexOf(val) !== -1) {
        //           isSearch.value = true;
        //           list.value.push(item);
        //         }
        //       });
        //     });
        //   });
        // }
        provinceCityAreaList.value = cloneDeep(list.value);
      } else {
        provinceCityAreaList.value = copyProvinceCityAreaList.value;
      }
    });

    // 回显
    watch(() => form.value, () => {
      form.value.regionInfoList!.forEach((info) => {
        info.cityInfoList!.forEach((item) => {
          checkedKeys.value.push(item.cityCode!);
          expandedKeys.value.push(item.cityCode!);
          console.log(item);
          if (!item.cityCode) {
            checkedKeys.value.push(info.provinceCode!);
            expandedKeys.value.push(info.provinceCode!);
          }
        });
      });
      checkedKeys.value = checkedKeys.value.filter(item => item);
      expandedKeys.value = expandedKeys.value.filter(item => item);
    });

    // 取消
    const handleCancel = async () => {
      await formRef.value?.resetFields();
      checkedKeys.value = [];
      expandedKeys.value = [];
      region.value = '';
      emit('update:modelValue', false);
    };

    // 提交
    const handleSubmit = async () => {
      try {
        await formRef.value?.validate();
        if (!props.regionId) {
          // 新增
          await regionAreaSaveApi(form.value);
        } else {
          // 编辑
          await regionAreaUpdateApi(form.value);
        }
        ElMessage.success('操作成功！');
        handleCancel();
        emit('handelSuccess');
      } catch (e) {
        console.log(e);
      }
    };

    return {
      form,
      formRef,
      treeRef,
      props: {
        label: 'label',
        children: 'children',
      },
      region,
      checkedKeys,
      expandedKeys,
      provinceCityAreaList,
      regionCodeList,

      handleCheck,
      handleSubmit,
      handleSelectRegion,
      handleCancel,
      handleOpen,
    };
  },
});
